// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useCallback, useState } from "react"
import { FlatList, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native"

import { Stack, useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { TMessage } from "types/transcript"

import { useChatHistoryDiagnostic } from "src/queries/useConversation"
import { computeMessageKey } from "src/utils/messageUtils"

// Add these types outside the component
type MessageDiff = {
  messageKey: string
  status: "inBoth" | "onlyInQuery" | "onlyInMmkv"
  queryMessage?: TMessage
  mmkvMessage?: TMessage
  differences: Record<string, { query: any; mmkv: any }>
}

type DiagnosticStats = {
  queryLength: number
  mmkvLength: number
  onlyInQueryCount: number
  onlyInMmkvCount: number
  totalUniqueCount: number
  queryMissingIds?: number
  mmkvMissingIds?: number
}

export default function DiagnosticsScreen() {
  const { chatHistoryDiagnostic, clearChatHistoryDiagnostic } = useChatHistoryDiagnostic()
  const [selectedDiagnostic, setSelectedDiagnostic] = useState<number | null>(null)

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const handleDiagnosticPress = (index: number) => {
    if (!chatHistoryDiagnostic || !chatHistoryDiagnostic[index]) return

    setSelectedDiagnostic(index === selectedDiagnostic ? null : index)
  }

  const handleClearDiagnostics = () => {
    clearChatHistoryDiagnostic()
    setSelectedDiagnostic(null)
  }

  // Move diff computation to a separate function
  const computeDiagnosticData = (
    index: number,
  ): { stats: DiagnosticStats; messageDiffs: MessageDiff[] } | null => {
    if (!chatHistoryDiagnostic || !chatHistoryDiagnostic[index]) return null

    const { queryTranscript, mmkvTranscript } = chatHistoryDiagnostic[index]

    // Create sets of message keys for comparison using computed message key
    const queryTranscriptSet = new Set(queryTranscript?.map((msg) => computeMessageKey(msg)) || [])
    const mmkvTranscriptSet = new Set(mmkvTranscript?.map((msg) => computeMessageKey(msg)) || [])

    // Find messages that exist only in query or only in MMKV
    const onlyInQuery = new Set(
      [...queryTranscriptSet].filter((messageKey) => !mmkvTranscriptSet.has(messageKey)),
    )
    const onlyInMmkv = new Set(
      [...mmkvTranscriptSet].filter((messageKey) => !queryTranscriptSet.has(messageKey)),
    )

    // Create a map of messages by message key for easy lookup
    const queryMessagesByKey = new Map(
      queryTranscript?.map((msg) => [computeMessageKey(msg), msg]) || [],
    )
    const mmkvMessagesByKey = new Map(
      mmkvTranscript?.map((msg) => [computeMessageKey(msg), msg]) || [],
    )

    // Get all unique message keys
    const allMessageKeys = new Set([...queryTranscriptSet, ...mmkvTranscriptSet])
    console.log("allMessageKeys", Array.from(allMessageKeys))

    // Calculate stats
    const stats: DiagnosticStats = {
      queryLength: queryTranscript?.length || 0,
      mmkvLength: mmkvTranscript?.length || 0,
      onlyInQueryCount: Array.from(onlyInQuery).length,
      onlyInMmkvCount: Array.from(onlyInMmkv).length,
      totalUniqueCount: Array.from(allMessageKeys).length,
    }

    // Compute message diffs
    const messageDiffs: MessageDiff[] = Array.from(allMessageKeys)
      .filter((messageKey): messageKey is string => messageKey !== undefined)
      .map((messageKey) => {
        const queryMessage = queryMessagesByKey.get(messageKey)
        const mmkvMessage = mmkvMessagesByKey.get(messageKey)

        // Determine message status
        let status: "inBoth" | "onlyInQuery" | "onlyInMmkv"

        if (queryMessage && mmkvMessage) {
          status = "inBoth"
        } else if (queryMessage) {
          status = "onlyInQuery"
        } else {
          status = "onlyInMmkv"
        }

        // Find differences in fields if message exists in both
        const differences: Record<string, { query: any; mmkv: any }> = {}

        if (queryMessage && mmkvMessage) {
          // Get all unique keys from both messages
          const allKeys = new Set([...Object.keys(queryMessage), ...Object.keys(mmkvMessage)])

          // Compare each field
          allKeys.forEach((key) => {
            const queryValue = queryMessage[key as keyof TMessage]
            const mmkvValue = mmkvMessage[key as keyof TMessage]

            // Skip if values are identical or both undefined
            if (JSON.stringify(queryValue) === JSON.stringify(mmkvValue)) return

            // Add to differences
            differences[key] = {
              query: queryValue,
              mmkv: mmkvValue,
            }
          })
        }

        return {
          messageKey,
          status,
          queryMessage,
          mmkvMessage,
          differences,
        }
      })

    return { stats, messageDiffs }
  }

  const renderDiagnosticDetails = (index: number) => {
    const diagnosticData = computeDiagnosticData(index)
    if (!diagnosticData) return null

    const { stats, messageDiffs } = diagnosticData

    return (
      <ScrollView style={styles.detailsContainer}>
        <Text style={styles.sectionTitle}>Transcript Comparison</Text>

        {/* Summary stats */}
        <View style={styles.statsContainer}>
          <Text style={styles.statItem}>Query Messages: {stats.queryLength}</Text>
          <Text style={styles.statItem}>MMKV Messages: {stats.mmkvLength}</Text>
          <Text style={styles.statItem}>Only in Query: {stats.onlyInQueryCount}</Text>
          <Text style={styles.statItem}>Only in MMKV: {stats.onlyInMmkvCount}</Text>
          <Text style={styles.statItem}>Total Unique Messages: {stats.totalUniqueCount}</Text>
        </View>

        {/* Message comparison */}
        {messageDiffs.map(({ messageKey, status, queryMessage, mmkvMessage, differences }) => {
          // Determine style based on status
          let messageStatus = ""
          let statusStyle = {}

          if (status === "inBoth") {
            messageStatus = "In Both"
            statusStyle = styles.inBothStatus
          } else if (status === "onlyInQuery") {
            messageStatus = "Only in Query"
            statusStyle = styles.onlyInQueryStatus
          } else {
            messageStatus = "Only in MMKV"
            statusStyle = styles.onlyInMmkvStatus
          }

          return (
            <View key={messageKey} style={styles.messageContainer}>
              <View style={styles.messageHeader}>
                <Text style={styles.messageId}>Message Key: {messageKey}</Text>
                <Text style={[styles.messageStatus, statusStyle]}>{messageStatus}</Text>
              </View>

              {/* Display regular ID for reference */}
              <View style={styles.idContainer}>
                {queryMessage && <Text style={styles.idText}>Query ID: {queryMessage.id}</Text>}
                {mmkvMessage && <Text style={styles.idText}>MMKV ID: {mmkvMessage.id}</Text>}
              </View>

              {Object.keys(differences).length > 0 ? (
                <View style={styles.differencesContainer}>
                  <Text style={styles.differencesTitle}>Field Differences:</Text>
                  {Object.entries(differences).map(([field, { query, mmkv }]) => (
                    <View key={field} style={styles.fieldDiff}>
                      <Text style={styles.fieldName}>{field}:</Text>
                      <View style={styles.fieldValues}>
                        <Text style={styles.queryValue}>
                          Query: {typeof query === "object" ? JSON.stringify(query) : String(query)}
                        </Text>
                        <Text style={styles.mmkvValue}>
                          MMKV: {typeof mmkv === "object" ? JSON.stringify(mmkv) : String(mmkv)}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              ) : (
                queryMessage &&
                mmkvMessage && <Text style={styles.noFieldDifferences}>No field differences</Text>
              )}

              {/* Show message content for messages that exist in only one place */}
              {queryMessage && !mmkvMessage && (
                <View style={styles.singleMessageContainer}>
                  <Text style={styles.singleMessageTitle}>Query Message Content:</Text>
                  <Text style={styles.messageContent}>{JSON.stringify(queryMessage, null, 2)}</Text>
                </View>
              )}

              {!queryMessage && mmkvMessage && (
                <View style={styles.singleMessageContainer}>
                  <Text style={styles.singleMessageTitle}>MMKV Message Content:</Text>
                  <Text style={styles.messageContent}>{JSON.stringify(mmkvMessage, null, 2)}</Text>
                </View>
              )}
            </View>
          )
        })}
      </ScrollView>
    )
  }

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ title: "Diagnostics" }} />

      <Text style={styles.title}>Chat History Diagnostics</Text>

      {!chatHistoryDiagnostic || chatHistoryDiagnostic.length === 0 ? (
        <Text style={styles.emptyText}>No diagnostic records available</Text>
      ) : (
        <>
          <FlatList
            data={chatHistoryDiagnostic}
            keyExtractor={(_, index) => `diagnostic-${index}`}
            renderItem={({ item, index }) => (
              <View>
                <TouchableOpacity
                  style={[
                    styles.diagnosticButton,
                    selectedDiagnostic === index && styles.selectedDiagnosticButton,
                  ]}
                  onPress={() => handleDiagnosticPress(index)}
                >
                  <Text style={styles.buttonText}>Diagnostic Record {index + 1}</Text>
                  <Text style={styles.timestampText}>
                    {item.queryTranscript && item.queryTranscript.length > 0
                      ? new Date(item.queryTranscript[0].created_at || "").toLocaleString()
                      : "No timestamp"}
                  </Text>
                  <Text style={styles.typeText}>Type: {item.type}</Text>
                </TouchableOpacity>

                {selectedDiagnostic === index && renderDiagnosticDetails(index)}
              </View>
            )}
          />

          <TouchableOpacity
            style={[styles.diagnosticButton, styles.clearButton]}
            onPress={handleClearDiagnostics}
          >
            <Text style={styles.buttonText}>Clear All Diagnostics</Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: "#f5f5f5",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 16,
    textAlign: "center",
    marginTop: 20,
    color: "#666",
  },
  diagnosticButton: {
    backgroundColor: "#007AFF",
    padding: 12,
    borderRadius: 8,
    marginBottom: 10,
  },
  selectedDiagnosticButton: {
    backgroundColor: "#0056b3",
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "500",
  },
  timestampText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: 12,
    marginTop: 4,
  },
  typeText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: 12,
    marginTop: 4,
  },
  clearButton: {
    backgroundColor: "#FF3B30",
    marginTop: 20,
  },
  detailsContainer: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    maxHeight: 500,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
  },
  statsContainer: {
    backgroundColor: "#f0f0f0",
    padding: 10,
    borderRadius: 6,
    marginBottom: 16,
  },
  statItem: {
    fontSize: 14,
    marginBottom: 4,
  },
  messageContainer: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  messageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  messageId: {
    fontWeight: "bold",
    fontSize: 14,
  },
  idContainer: {
    marginBottom: 8,
  },
  idText: {
    fontSize: 12,
    color: "#666",
  },
  messageStatus: {
    fontSize: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  inBothStatus: {
    backgroundColor: "#e6f7ff",
    color: "#0070f3",
  },
  onlyInQueryStatus: {
    backgroundColor: "#fff7e6",
    color: "#fa8c16",
  },
  onlyInMmkvStatus: {
    backgroundColor: "#f6ffed",
    color: "#52c41a",
  },
  differencesContainer: {
    marginTop: 8,
  },
  differencesTitle: {
    fontWeight: "bold",
    marginBottom: 6,
  },
  fieldDiff: {
    marginBottom: 8,
  },
  fieldName: {
    fontWeight: "bold",
    fontSize: 13,
  },
  fieldValues: {
    marginLeft: 10,
  },
  queryValue: {
    color: "#fa8c16",
    fontSize: 12,
  },
  mmkvValue: {
    color: "#52c41a",
    fontSize: 12,
  },
  noFieldDifferences: {
    fontStyle: "italic",
    color: "#888",
    marginTop: 4,
  },
  singleMessageContainer: {
    marginTop: 8,
  },
  singleMessageTitle: {
    fontWeight: "bold",
    marginBottom: 4,
  },
  messageContent: {
    fontSize: 12,
    fontFamily: "monospace",
    backgroundColor: "#f5f5f5",
    padding: 8,
    borderRadius: 4,
  },
})
